import classNames from 'classnames';
import { Portal, svgSprite, Tooltip } from '../../../../../../hybrid/wrappers';
import css from './BinarySettings.module.scss';
import T, { t } from '../../../../../../common/components/T';
import { useCallback, useContext, useEffect, useState } from 'react';
import { LangContext } from '../../../../../../common/decorators/Language';
import Skeleton from '../../../../../plug/Skeleton/Skeleton';
import UserContext, { UserProfileContext } from '../../../../../../common/utils/getContext';
import { dataSender, getData } from '../../../../../../common/utils/router';
import { useWindowDimensions } from '../../../../../../common/utils/windowDimensions';
import FormButtons from '../../../../form-control-buttons/FormButtons/FormButtons';
import { API_SERVER_URL } from '../../../../../../common/utils/utils';

const RenderData = ({ response, closeHandler, isMobile }) => {
    const { setAlertData, userToken, lang } = useContext(UserContext);
    const { translation } = useContext(LangContext);
    const [active, setActive] = useState(response?.filling_settings);

    const buttons = [
        {
            title: t('balanced', 'Сбалансировано', 'all', translation),
            value: 0
        },
        {
            title: t('to_left_branch', 'В левую ветку', 'all', translation),
            value: 1
        },
        {
            title: t('to_right_branch', 'В правую ветку', 'all', translation),
            value: 2
        }
    ];

    const boldText1 = (
        <span className={css.bold}>{t('balanced_with_quotation_marks', '«Сбалансировано»', 'all', translation)}</span>
    );
    const boldText2 = (
        <span className={css.bold}>
            {t('right_of_left_branch_with_quotation_marks', '«В левую ветку» или «В правую ветку»', 'all', 'translation')}
        </span>
    );

    const changeSetting = (setting_id) => {
        dataSender(
            `${API_SERVER_URL}/api/v1/user/marketing/nodes/${response?.id}/filling-settings`,
            'PATCH',
            { filling_settings: setting_id },
            () => {},
            () => {
                setAlertData(false);
            },
            userToken,
            lang
        );
        closeHandler();
    };

    return (
        <div>
            <div className={css.treeDropSettingHeader}>
                <span>{t('setting_up_cell_placement', 'Настройка постановки ячеек', 'all', translation)}</span>
                <span className={css.treeDropSettingHeader_tooltip}>
                    <Tooltip
                        className={'treegraphBranchSettingsTooltip'}
                        text={
                            <div>
                                <p className={classNames(css.infoText, css.infoTextTitle)}>
                                    {t('using_the_staging_setting', 'Использование настройки постановки', 'all', translation)}
                                </p>
                                <p className={css.infoText}>
                                    {t(
                                        'customize_the_binary_structure_yourself',
                                        'Настраивайте постановку в бинарную структуру самостоятельно.',
                                        'all',
                                        translation
                                    )}
                                </p>
                                <p className={css.infoText}>
                                    <T
                                        vars={{
                                            boldText: boldText1
                                        }}
                                        textName='binary_settings_tooltip_text_1'
                                        defaultStr='${boldText} - пользователи будут становиться в ту ветку, в которой меньше объем.'
                                        page='all'
                                    />
                                </p>
                                <p className={css.infoText}>
                                    <T
                                        vars={{
                                            boldText: boldText2
                                        }}
                                        textName='binary_settings_tooltip_text_2'
                                        defaultStr='${boldText} - пользователи будут становиться в ветку, соответствующую вашему выбору.'
                                        page='all'
                                    />
                                </p>
                            </div>
                        }
                        compStyle={{
                            inner: {
                                className: css.infoIcon,
                                top: '2px'
                            }
                        }}
                    >
                        {svgSprite('question-circle-fill', {
                            className: css.infoSpeedProgressTooltipIcon
                        })}
                    </Tooltip>
                </span>
            </div>
            <div className={css.dropBody}>
                {buttons?.map((button, idx) => (
                    <div
                        key={`btnStatsw${idx}`}
                        className={classNames(css.orderSettingsContentItem, active === idx && css.orderSettingsContentItemActive)}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActive(idx);
                            if (!isMobile) {
                                changeSetting(idx);
                            }
                        }}
                    >
                        <div className={active === idx ? css.orderSettingsContentRadioActive : css.orderSettingsContentRadio}>
                            <div
                                className={
                                    active === idx ? css.orderSettingsContentRadioInnerActive : css.orderSettingsContentRadioInner
                                }
                            ></div>
                        </div>
                        <div>{button.title}</div>
                    </div>
                ))}
            </div>
            <div className={css.mobileButtons}>
                <FormButtons
                    sendFormData={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        changeSetting(active);
                    }}
                    buttons={[
                        {
                            type: 'cancel',
                            clickFunc: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                closeHandler();
                            }
                        },
                        {
                            type: 'ok',
                            buttonText: <T textName='save' defaultStr='Сохранить' />
                        }
                    ]}
                />
            </div>
        </div>
    );
};

const BinarySettings = () => {
    const { userToken, lang } = useContext(UserContext);
    const { profileData } = useContext(UserProfileContext);
    const [settingModal, showSettingModal] = useState(false);

    const screenWidth = useWindowDimensions();
    const [isMobile, setIsMobile] = useState(screenWidth < 768);
    useEffect(() => {
        setIsMobile(screenWidth < 768);
    }, [screenWidth]);

    const loader = useCallback(async () => {
        const user_id = profileData?.id;
        if (user_id) {
            const data = await getData(`/api/v1/user/marketing/structures/${user_id}`, userToken, lang);
            return data?.data?.[0]?.nodes?.[0];
        }
        return null;
    }, [userToken, lang, profileData]);

    const Wrapper = useCallback(
        ({ children }) => {
            if (isMobile) {
                return <Portal customClass='modalBottom'>{children}</Portal>;
            } else {
                return <>{children}</>;
            }
        },
        [isMobile]
    );

    return (
        <div
            style={{ position: 'relative' }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                showSettingModal((s) => !s);
            }}
        >
            <Tooltip
                text={<T textName='setting_up_a_production' defaultStr='Настройка постановки' />}
                compStyle={{
                    tooltipWrap: {
                        className: classNames(css.copyInputIconWrap)
                    },
                    inner: {
                        className: classNames(css.formControlIconWrap)
                    }
                }}
                disableOnMobile={true}
            >
                {svgSprite('settings', {
                    className: css.copyTypeIcon
                })}
            </Tooltip>
            {settingModal ? (
                <Wrapper>
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        className={css.settingModal}
                    >
                        <Skeleton
                            handler={loader}
                            Component={RenderData}
                            componentProps={{
                                closeHandler: () => showSettingModal(false),
                                isMobile
                            }}
                            elements={['text', 'empty', 'blockSM', 'blockSM', 'blockSM']}
                        />
                    </div>
                </Wrapper>
            ) : null}
        </div>
    );
};

export default BinarySettings;
